/**
 * MAIZ Motion Engine v1.0
 * Foundation JS for the MAIZ Design System.
 * Systems: Reveal · Parallax · 3D Tilt · Navigation · Cart · Search
 *          Hero Slideshow · Hotspots · Carousel · Mobile Nav · Counters
 */
(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────
     UTILITIES
  ────────────────────────────────────────────────────── */

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  const lerp = (a, b, t) => a + (b - a) * t;

  function debounce(fn, wait) {
    let id;
    return (...args) => { clearTimeout(id); id = setTimeout(() => fn(...args), wait); };
  }

  function formatMoney(cents) {
    const isWhole = cents % 100 === 0;
    return '₪' + (cents / 100).toLocaleString('he-IL', {
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: isWhole ? 0 : 2,
    });
  }
  window.formatMoney = formatMoney; // single shared money formatter, reused by the separate Commerce IIFE below

  /* Easing — easeOutQuart for counters */
  const easeOutQuart = t => 1 - Math.pow(1 - t, 4);

  /* Prefixed: add is-visible to element */
  function reveal(el) {
    el.classList.add('is-visible');
  }

  /* Check if reduced motion is preferred */
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Check if device is touch / mobile */
  const isMobile = () => window.innerWidth < 768;
  const isTouch  = () => window.matchMedia('(hover: none)').matches;


  /* ──────────────────────────────────────────────────────
     1. INTERSECTION OBSERVER — REVEAL SYSTEM
  ────────────────────────────────────────────────────── */

  function initReveal() {
    if (reducedMotion) {
      $$('.maiz-reveal, .maiz-sep, .maiz-reveal-stagger').forEach(reveal);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          reveal(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    $$('.maiz-reveal, .maiz-sep, .maiz-reveal-stagger').forEach(el => {
      observer.observe(el);
    });
  }


  /* ──────────────────────────────────────────────────────
     2. COUNTER ANIMATION
  ────────────────────────────────────────────────────── */

  function initCounters() {
    if (reducedMotion) return;

    const DURATION = 1500;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);

        const el = entry.target;
        const rawTarget = el.dataset.target || '0';
        const target = parseFloat(rawTarget.replace(/,/g, ''));
        const suffix = el.dataset.suffix || '';
        const isFloat = rawTarget.includes('.');

        if (isNaN(target)) return;

        const start = performance.now();
        const step = (now) => {
          const elapsed = now - start;
          const t = Math.min(elapsed / DURATION, 1);
          const val = easeOutQuart(t) * target;
          if (isFloat) {
            el.textContent = val.toFixed(1) + suffix;
          } else {
            el.textContent = Math.floor(val).toLocaleString('he-IL') + suffix;
          }
          if (t < 1) requestAnimationFrame(step);
          else el.textContent = (isFloat ? target.toFixed(1) : target.toLocaleString('he-IL')) + suffix;
        };
        requestAnimationFrame(step);
      });
    }, { threshold: 0.5 });

    $$('[data-target].maiz-counter').forEach(el => observer.observe(el));
  }


  /* ──────────────────────────────────────────────────────
     3. PARALLAX ENGINE
  ────────────────────────────────────────────────────── */

  function initParallax() {
    if (reducedMotion || isMobile()) return;

    const els = $$('[data-parallax]').map(el => ({
      el,
      factor: parseFloat(el.dataset.parallax) || 0.3,
    }));

    if (!els.length) return;

    let ticking = false;

    const update = () => {
      els.forEach(({ el, factor }) => {
        const parent = el.parentElement;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * factor;
        el.style.transform = `translateY(${offset}px)`;
      });
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update(); // initial position
  }


  /* ──────────────────────────────────────────────────────
     4. 3D TILT ENGINE
  ────────────────────────────────────────────────────── */

  class TiltCard {
    constructor(el, options = {}) {
      this.el      = el;
      this.maxX    = options.maxX || 2.5;
      this.maxY    = options.maxY || 2;
      this.smooth  = options.smooth || 0.1;
      this.curX    = 0;
      this.curY    = 0;
      this.tgtX    = 0;
      this.tgtY    = 0;
      this.rafId   = null;
      this._onMove  = this._onMove.bind(this);
      this._onLeave = this._onLeave.bind(this);
      this.el.addEventListener('mousemove', this._onMove);
      this.el.addEventListener('mouseleave', this._onLeave);
    }

    _onMove(e) {
      const rect = this.el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      this.tgtY = (x - 0.5) * this.maxY * 2;
      this.tgtX = -(y - 0.5) * this.maxX * 2;
      if (!this.rafId) this._tick();
    }

    _onLeave() {
      this.tgtX = 0;
      this.tgtY = 0;
    }

    _tick() {
      this.curX = lerp(this.curX, this.tgtX, this.smooth);
      this.curY = lerp(this.curY, this.tgtY, this.smooth);

      const atRest = (
        Math.abs(this.curX) < 0.01 &&
        Math.abs(this.curY) < 0.01 &&
        this.tgtX === 0 &&
        this.tgtY === 0
      );

      if (atRest) {
        this.el.style.transform = '';
        this.rafId = null;
        return;
      }

      this.el.style.transform =
        `perspective(800px) rotateX(${this.curX}deg) rotateY(${this.curY}deg)`;
      this.rafId = requestAnimationFrame(() => this._tick());
    }

    destroy() {
      this.el.removeEventListener('mousemove', this._onMove);
      this.el.removeEventListener('mouseleave', this._onLeave);
      cancelAnimationFrame(this.rafId);
    }
  }

  function initTilt() {
    if (reducedMotion || isTouch()) return;

    $$('[data-tilt]').forEach(el => new TiltCard(el, {
      maxX: parseFloat(el.dataset.tiltMaxX) || 2.5,
      maxY: parseFloat(el.dataset.tiltMaxY) || 2,
    }));
  }


  /* ──────────────────────────────────────────────────────
     5. NAVIGATION — transparent → glass on scroll
  ────────────────────────────────────────────────────── */

  function initNav() {
    const nav = $('.maiz-nav');
    if (!nav) return;

    const SCROLL_THRESHOLD = 80;
    let isGlass = false;

    const update = () => {
      const shouldBeGlass = window.scrollY > SCROLL_THRESHOLD;
      if (shouldBeGlass === isGlass) return;
      isGlass = shouldBeGlass;

      if (shouldBeGlass) {
        nav.classList.add('maiz-nav--glass');
        nav.classList.remove('maiz-nav--transparent');
      } else {
        nav.classList.remove('maiz-nav--glass');
        nav.classList.add('maiz-nav--transparent');
      }
    };

    window.addEventListener('scroll', update, { passive: true });
    update(); // initial state

    /* Mega menu */
    $$('[data-mega-trigger]').forEach(trigger => {
      const menuId = trigger.dataset.megaTrigger;
      const menu = $(`[data-mega="${menuId}"]`);
      if (!menu) return;

      let openTimer, closeTimer;

      const open = () => {
        clearTimeout(closeTimer);
        openTimer = setTimeout(() => menu.classList.add('is-open'), 150);
      };

      const close = () => {
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => menu.classList.remove('is-open'), 100);
      };

      trigger.addEventListener('mouseenter', open);
      trigger.addEventListener('mouseleave', close);
      menu.addEventListener('mouseenter', open);
      menu.addEventListener('mouseleave', close);

      /* Keyboard */
      trigger.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          menu.classList.contains('is-open') ? close() : open();
        }
        if (e.key === 'Escape') close();
      });
    });

    /* Mega menu — dynamic category image panel */
    const megaImageMapEl = $('#MegaMenuImageMap');
    const megaImageEl    = $('#MegaMenuImage');
    if (megaImageMapEl && megaImageEl) {
      let megaImageMap = {};
      try { megaImageMap = JSON.parse(megaImageMapEl.textContent); } catch (e) { /* noop */ }

      const defaultSrc = megaImageEl.getAttribute('src');
      const swapTo = key => {
        const url = megaImageMap[key];
        if (!url || megaImageEl.getAttribute('src') === url) return;
        megaImageEl.style.opacity = '0';
        setTimeout(() => {
          megaImageEl.setAttribute('src', url);
          megaImageEl.style.opacity = '1';
        }, 120);
      };

      $$('#MegaMenuCollections [data-mega-img]').forEach(link => {
        const key = link.dataset.megaImg;
        if (!key) return;
        link.addEventListener('mouseenter', () => swapTo(key));
        link.addEventListener('focus', () => swapTo(key));
      });

      /* Reset to default once the whole mega menu closes */
      const megaMenuEl = $('#MegaMenuCollections');
      if (megaMenuEl) {
        megaMenuEl.addEventListener('mouseleave', () => {
          setTimeout(() => {
            if (!megaMenuEl.classList.contains('is-open')) megaImageEl.setAttribute('src', defaultSrc);
          }, 150);
        });
      }
    }
  }


  /* ──────────────────────────────────────────────────────
     6. MOBILE NAVIGATION
  ────────────────────────────────────────────────────── */

  function initMobileNav() {
    const hamburger = $('.maiz-nav__hamburger');
    const mobileNav = $('.maiz-mobile-nav');
    const closeBtn  = $('.maiz-mobile-nav__close');

    if (!hamburger || !mobileNav) return;

    const open = () => {
      mobileNav.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      hamburger.setAttribute('aria-expanded', 'true');
      mobileNav.removeAttribute('aria-hidden');
    };

    const close = () => {
      mobileNav.classList.remove('is-open');
      document.body.style.overflow = '';
      hamburger.setAttribute('aria-expanded', 'false');
      mobileNav.setAttribute('aria-hidden', 'true');
    };

    hamburger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);

    /* Close on overlay tap */
    mobileNav.addEventListener('click', e => {
      if (e.target === mobileNav) close();
    });

    /* Escape key */
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) close();
    });

    /* Mobile nav — dynamic category stage image, driven by whichever
       accordion group is currently expanded (tap-to-select on mobile). */
    const mobileImageMapEl = $('#MobileNavImageMap');
    const stageEl          = $('#MobileNavStage');
    const stageImgEl       = $('#MobileNavStageImg');
    const megaToggles      = $$('.maiz-mobile-nav__toggle[data-mega-img]');

    if (mobileImageMapEl && stageEl && stageImgEl && megaToggles.length) {
      let mobileImageMap = {};
      try { mobileImageMap = JSON.parse(mobileImageMapEl.textContent); } catch (e) { /* noop */ }

      const syncStage = () => {
        const openToggles = megaToggles.filter(d => d.open);
        if (!openToggles.length) {
          stageEl.classList.remove('is-visible');
          return;
        }
        const active = openToggles[openToggles.length - 1];
        const url = mobileImageMap[active.dataset.megaImg];
        if (url) {
          stageImgEl.setAttribute('src', url);
          stageEl.classList.add('is-visible');
        }
      };

      megaToggles.forEach(details => details.addEventListener('toggle', syncStage));

      /* Collapse everything when the mobile nav itself closes, so it
         reopens fresh next time rather than mid-state. */
      mobileNav.addEventListener('transitionend', e => {
        if (e.target !== mobileNav || mobileNav.classList.contains('is-open')) return;
        megaToggles.forEach(d => { d.open = false; });
        stageEl.classList.remove('is-visible');
      });
    }
  }


  /* ──────────────────────────────────────────────────────
     7. HERO SLIDESHOW — Ken Burns + blur-dissolve
  ────────────────────────────────────────────────────── */

  function initHero() {
    const hero = $('.maiz-hero');
    if (!hero) return;

    const slides    = $$('.maiz-hero__slide', hero);
    const dots      = $$('.maiz-hero__dot', hero);
    const rooms     = $$('.maiz-hero__room', hero);
    const counterEl = $('[data-hero-counter]', hero);
    const prevBtn   = $('.maiz-hero__arrow--prev', hero);
    const nextBtn   = $('.maiz-hero__arrow--next', hero);
    if (slides.length < 2) {
      /* Single slide — just reveal content */
      revealHeroContent(slides[0]);
      return;
    }

    let current = 0;
    let timer   = null;

    const SLIDE_DURATION  = 8000;  // ms between slides
    const BLUR_OUT        = 400;   // blur out duration
    const CROSS_FADE      = 200;   // cross-fade overlap
    const BLUR_IN         = 300;   // blur in duration

    function goTo(idx, immediate = false) {
      const prev = slides[current];
      const next = slides[idx];

      if (reducedMotion || immediate) {
        slides.forEach((s, i) => {
          s.classList.toggle('is-active', i === idx);
          s.classList.remove('is-prev');
        });
        current = idx;
        updateDots();
        return;
      }

      /* Blur-dissolve transition */
      prev.classList.add('is-prev');
      prev.classList.remove('is-active');
      next.classList.add('is-active');

      const prevImg = $('.maiz-hero__image', prev);
      const nextImg = $('.maiz-hero__image', next);

      /* Reset Ken Burns on incoming slide */
      if (nextImg) {
        nextImg.style.animationName = 'none';
        nextImg.offsetHeight; // reflow to reset
        nextImg.style.animationName = '';
      }

      /* Phase 1: blur out prev */
      if (prevImg) prevImg.style.filter = `blur(0px)`;

      requestAnimationFrame(() => {
        if (prevImg) {
          prevImg.style.transition = `filter ${BLUR_OUT}ms ease, opacity ${CROSS_FADE}ms ease`;
          prevImg.style.filter   = 'blur(8px)';
          prevImg.style.opacity  = '0';
        }
      });

      /* Phase 2: cross-fade, then blur in next */
      setTimeout(() => {
        prev.classList.remove('is-prev');
        if (prevImg) {
          prevImg.style.transition = '';
          prevImg.style.filter     = '';
          prevImg.style.opacity    = '';
        }
        if (nextImg) {
          nextImg.style.filter = 'blur(8px)';
          nextImg.style.opacity = '0';
          requestAnimationFrame(() => {
            nextImg.style.transition = `filter ${BLUR_IN}ms ease, opacity ${BLUR_IN}ms ease`;
            nextImg.style.filter     = 'blur(0px)';
            nextImg.style.opacity    = '1';
          });
          setTimeout(() => {
            nextImg.style.transition = '';
            nextImg.style.filter     = '';
            nextImg.style.opacity    = '';
          }, BLUR_IN + 50);
        }
      }, BLUR_OUT + CROSS_FADE);

      current = idx;
      updateDots();
      revealHeroContent(next);
    }

    function next() {
      goTo((current + 1) % slides.length);
    }

    function updateDots() {
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
      rooms.forEach((r, i) => {
        r.classList.toggle('is-active', i === current);
        r.setAttribute('aria-selected', i === current ? 'true' : 'false');
      });
      if (counterEl) {
        const pad = n => String(n).padStart(2, '0');
        counterEl.innerHTML = '<b>' + pad(current + 1) + '</b> / ' + pad(slides.length);
      }
    }

    function startTimer() {
      stopTimer();
      timer = setInterval(next, SLIDE_DURATION);
    }

    function stopTimer() {
      clearInterval(timer);
    }

    /* Dots */
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        goTo(i);
        stopTimer();
        startTimer();
      });
    });

    /* Room rail (Dreams & Solutions navigator) */
    rooms.forEach((room, i) => {
      room.addEventListener('click', () => {
        if (i === current) return;
        goTo(i);
        stopTimer();
        startTimer();
      });
    });

    /* Prev / next arrows */
    if (prevBtn) prevBtn.addEventListener('click', () => { goTo((current - 1 + slides.length) % slides.length); stopTimer(); startTimer(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { goTo((current + 1) % slides.length); stopTimer(); startTimer(); });

    /* Pause on hover */
    hero.addEventListener('mouseenter', stopTimer);
    hero.addEventListener('mouseleave', startTimer);

    /* Init first slide */
    goTo(0, true);
    revealHeroContent(slides[0]);
    startTimer();
  }

  /* Policy pages: Shopify renders /policies/* with fixed markup that
     bypasses theme templates. Localize titles to Hebrew. */
  function initPolicyPage() {
    const container = $('.shopify-policy__container');
    if (!container) return;
    const titles = {
      'Shipping policy': 'מדיניות משלוחים',
      'Refund policy': 'מדיניות החזרות וביטולים',
      'Privacy policy': 'מדיניות פרטיות',
      'Terms of service': 'תקנון האתר',
      'Contact information': 'פרטי התקשרות',
      'Subscription policy': 'מדיניות מנויים',
    };
    const h1 = $('.shopify-policy__title h1', container) || $('h1', container);
    if (h1) {
      const he = titles[h1.textContent.trim()];
      if (he) {
        h1.textContent = he;
        document.title = he + ' | MAIZ';
      }
    }
  }

  /* Chip strip scroll arrow */
  function initChipStrip() {
    $$('[data-section-type="chip-strip"]').forEach(strip => {
      const track = $('.maiz-chips__track', strip);
      const btn = $('[data-chips-next]', strip);
      if (!track || !btn) return;
      btn.addEventListener('click', () => {
        /* RTL track: advance = scroll toward the left */
        track.scrollBy({ left: -320, behavior: 'smooth' });
      });
    });
  }

  function revealHeroContent(slide) {
    if (!slide) return;

    /* Stagger headline words */
    const words = $$('.maiz-hero__word', slide);
    words.forEach((word, i) => {
      setTimeout(() => {
        word.classList.add('is-visible');
      }, 800 + i * 60);
    });

    /* CTA */
    const cta = $('.maiz-hero__cta', slide);
    if (cta) {
      setTimeout(() => cta.classList.add('is-visible'), 800 + words.length * 60 + 200);
    }
  }


  /* ──────────────────────────────────────────────────────
     8. HOTSPOT SYSTEM
  ────────────────────────────────────────────────────── */

  function initHotspots() {
    const allHotspots = $$('.maiz-hotspot');
    const closeAll = () => allHotspots.forEach(h => {
      h.classList.remove('is-open');
      h.setAttribute('aria-expanded', 'false');
    });

    allHotspots.forEach(hotspot => {
      const card = $('.maiz-hotspot__card', hotspot);
      if (!card) return;

      const openHotspot = () => {
        closeAll();
        hotspot.classList.add('is-open');
        hotspot.setAttribute('aria-expanded', 'true');
      };
      const closeHotspot = () => {
        hotspot.classList.remove('is-open');
        hotspot.setAttribute('aria-expanded', 'false');
      };

      if (isTouch()) {
        hotspot.addEventListener('click', e => {
          e.stopPropagation();
          hotspot.classList.contains('is-open') ? closeHotspot() : openHotspot();
        });
        document.addEventListener('click', closeAll);
      } else {
        hotspot.addEventListener('mouseenter', openHotspot);
        hotspot.addEventListener('mouseleave', closeHotspot);
        /* Keyboard support for role="button" div */
        hotspot.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            hotspot.classList.contains('is-open') ? closeHotspot() : openHotspot();
          }
          if (e.key === 'Escape') closeHotspot();
        });
      }
    });
  }


  /* ──────────────────────────────────────────────────────
     9. CAROUSEL
  ────────────────────────────────────────────────────── */

  function initCarousels() {
    $$('.maiz-carousel').forEach(carousel => {
      const track   = $('.maiz-carousel__track', carousel);
      const prevBtn = $('.maiz-carousel__arrow--prev', carousel);
      const nextBtn = $('.maiz-carousel__arrow--next', carousel);

      if (!track) return;

      /* Apply responsive CSS variables for item sizing */
      const itemsDesktop = parseInt(carousel.dataset.itemsDesktop) || 3;
      const itemsTablet  = parseInt(carousel.dataset.itemsTablet)  || 2;
      const itemsMobile  = parseInt(carousel.dataset.itemsMobile)  || 1;
      const gap          = parseInt(carousel.dataset.gap)           || 24;

      carousel.style.setProperty('--items-desktop', itemsDesktop);
      carousel.style.setProperty('--items-tablet',  itemsTablet);
      carousel.style.setProperty('--items-mobile',  itemsMobile);
      carousel.style.setProperty('--carousel-gap',  gap + 'px');

      const getScrollAmount = () => {
        const item = track.querySelector('.maiz-carousel__item');
        if (!item) return 300;
        const gapPx = parseFloat(getComputedStyle(track).gap) || gap;
        return item.offsetWidth + gapPx;
      };

      const updateArrows = () => {
        if (!prevBtn || !nextBtn) return;
        /* RTL: scrollLeft is negative in Chrome, positive in Firefox */
        const sl = Math.abs(track.scrollLeft);
        prevBtn.disabled = sl <= 2;
        nextBtn.disabled = sl + track.clientWidth >= track.scrollWidth - 2;
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          /* RTL: positive scrollBy = scroll toward right = "previous" items */
          track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
          setTimeout(updateArrows, 400);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
          setTimeout(updateArrows, 400);
        });
      }

      track.addEventListener('scroll', debounce(updateArrows, 100), { passive: true });

      /* Touch/swipe support */
      let touchStartX = 0;
      track.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
      track.addEventListener('touchend', e => {
        const delta = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 40) setTimeout(updateArrows, 400);
      }, { passive: true });

      updateArrows();
    });
  }


  /* ──────────────────────────────────────────────────────
     10. HEADER SEARCH — attached panel (v3)
     Live results are NOT predictive search (blocked for Hebrew stores —
     see project notes: Shopify's Predictive Search API only supports 39
     languages and Hebrew isn't one of them, confirmed via shopify.dev).
     Instead this fetches the real /search results page through the Section
     Rendering API and reuses the exact rendered .pcard markup — the same
     snippets/product-card.liquid every collection page already renders.
     No card markup, pricing, or badge logic is duplicated here.
  ────────────────────────────────────────────────────── */

  /* ── Recently Viewed + Recent Searches (this browser only, via localStorage) ──
     Recently Viewed stores a small display snapshot (title/image/price/vendor)
     captured from the live product page at view time, so the default search
     panel can render it instantly with zero extra network requests. Recent
     Searches stores this visitor's own past query strings — there is no
     storefront API for real cross-visitor "popular searches" analytics, so
     rather than inventing a static list this shows the visitor's own genuine
     history instead (same verified-data-only principle applied to badges). */

  const RECENTLY_VIEWED_KEY        = 'maiz_recently_viewed';
  const RECENTLY_VIEWED_MAX_STORED = 12;
  const RECENTLY_VIEWED_MAX_SHOWN  = 6;
  const RECENT_SEARCHES_KEY        = 'maiz_recent_searches';
  const RECENT_SEARCHES_MAX_STORED = 10;
  const RECENT_SEARCHES_MAX_SHOWN  = 6;

  function readList(key) {
    try {
      const list = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(list) ? list : [];
    } catch { return []; }
  }
  function writeList(key, list) {
    try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* storage unavailable/full — skip silently */ }
  }

  function recordRecentlyViewed() {
    const p = window.MAIZ && window.MAIZ.currentProduct;
    if (!p || !p.id) return;
    const list = readList(RECENTLY_VIEWED_KEY).filter(item => item.id !== p.id);
    list.unshift(p);
    writeList(RECENTLY_VIEWED_KEY, list.slice(0, RECENTLY_VIEWED_MAX_STORED));
  }

  function recordRecentSearch(query) {
    const q = query.trim();
    if (!q) return;
    const list = readList(RECENT_SEARCHES_KEY).filter(t => t.toLowerCase() !== q.toLowerCase());
    list.unshift(q);
    writeList(RECENT_SEARCHES_KEY, list.slice(0, RECENT_SEARCHES_MAX_STORED));
  }

  /* DELIBERATE PERFORMANCE EXCEPTION — read before editing product-card.liquid.
     ─────────────────────────────────────────────────────────────────────
     Every other card in the storefront (collection pages, and the live
     search results in initHeaderSearch above) is real server-rendered
     .pcard markup fetched via the Section Rendering API — zero duplicate
     markup. This function is the one deliberate exception: it rebuilds a
     .pcard node client-side from a localStorage snapshot instead.

     Why: measured (2026-08-01) against this store's live Section Rendering
     endpoint, fetching one product-card per recently-viewed item costs
     ~400-730ms wall-clock for up to 6 parallel requests (per-request
     latency is dominated by Shopify's routing/Liquid-render overhead, not
     payload size, so a purpose-built minimal section would not meaningfully
     change this). That cost would show up as either a visible loading
     delay every time the search panel opens (previously instant) or added
     server load on every storefront pageview if prefetched in the
     background. Both were judged not worth it for this feature, so this
     function stays a client-side reconstruction, evaluated and approved
     as an explicit, scoped trade-off rather than an oversight.

     Consequence: this HTML must be kept in sync by hand. It mirrors
     snippets/product-card.liquid's markup/classes exactly (pcard,
     pcard-img, pcard-badges, pcard-body, pcard-brand, pcard-name,
     price-row) so it renders through the exact same CSS — no separate
     visual system — and reuses formatMoney() plus the same round-half-up
     badge-percentage math as the Liquid card, so pricing/discount logic
     itself is not duplicated, only its HTML shape.

     ACTION REQUIRED: whenever snippets/product-card.liquid or
     snippets/price.liquid changes materially (new badge type, restructured
     markup/classes, changed discount math), review this function and
     update it to match by hand — nothing will catch drift automatically. */
  function buildRecentlyViewedCard(p) {
    const isOut = p.available === false;
    const hasSale = !isOut && p.compareAtPrice && p.compareAtPrice > p.price;
    const pct = hasSale ? Math.round((p.compareAtPrice - p.price) / p.compareAtPrice * 100) : 0;

    const article = document.createElement('article');
    article.className = 'pcard';
    article.dataset.productId = p.id;

    const brandHtml = (p.vendor && p.vendor !== 'עולם הסאונד' && p.vendor !== 'Olam HaSound')
      ? `<div class="pcard-brand"><span>${escapeHtml(p.vendor)}</span></div>` : '';
    const imgHtml = p.image
      ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy" width="600" height="450">`
      : '<span class="img-ph" aria-hidden="true"><span class="ic">🔊</span></span>';
    const priceHtml = hasSale
      ? `<span class="price-now">${formatMoney(p.price)}</span><span class="price-old">${formatMoney(p.compareAtPrice)}</span><span class="price-save">חיסכון ${formatMoney(p.compareAtPrice - p.price)}</span>`
      : `<span class="price-now">${formatMoney(p.price)}</span>`;

    article.innerHTML = `
      <a class="pcard-img" href="${escapeHtml(p.url)}" aria-label="${escapeHtml(p.title)}">
        <div class="pcard-badges">
          ${hasSale ? `<span class="badge badge-sale">-${pct}%</span>` : ''}
          ${isOut ? '<span class="badge badge-out">אזל</span>' : ''}
        </div>
        ${imgHtml}
      </a>
      <div class="pcard-body">
        ${brandHtml}
        <a class="pcard-name" href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a>
        <div class="price-row">${priceHtml}</div>
      </div>`;
    return article;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function initHeaderSearch() {
    const wrap = $('#SearchPanelWrap');
    if (!wrap) return;

    const input       = $('#SearchOverlayInput', wrap);
    const closeBtn     = $('.maiz-search-bar__close', wrap);
    const defaultBlock = $('[data-search-default]', wrap);
    const statusEl     = $('[data-search-status]', wrap);
    const countEl      = $('[data-search-count]', wrap);
    const resultsEl    = $('[data-search-results]', wrap);
    const ctaEl        = $('[data-search-cta]', wrap);
    const triggers     = $$('[data-search-trigger]');

    const recentViewedBlock   = $('[data-recently-viewed]', wrap);
    const recentViewedGrid    = $('[data-recently-viewed-grid]', wrap);
    const recentSearchesBlock = $('[data-recent-searches]', wrap);
    const recentSearchesChips = $('[data-recent-searches-chips]', wrap);

    const MAX_RESULTS = 6;

    function renderRecentDefaults() {
      if (recentViewedBlock && recentViewedGrid) {
        const currentId = window.MAIZ && window.MAIZ.currentProduct && window.MAIZ.currentProduct.id;
        const items = readList(RECENTLY_VIEWED_KEY).filter(p => p.id !== currentId).slice(0, RECENTLY_VIEWED_MAX_SHOWN);
        recentViewedGrid.innerHTML = '';
        if (items.length) {
          items.forEach(p => recentViewedGrid.appendChild(buildRecentlyViewedCard(p)));
          recentViewedBlock.hidden = false;
        } else {
          recentViewedBlock.hidden = true;
        }
      }

      if (recentSearchesBlock && recentSearchesChips) {
        const terms = readList(RECENT_SEARCHES_KEY).slice(0, RECENT_SEARCHES_MAX_SHOWN);
        recentSearchesChips.innerHTML = '';
        if (terms.length) {
          terms.forEach(term => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'maiz-search-chip';
            chip.textContent = term;
            chip.addEventListener('click', () => {
              input.value = term;
              input.focus();
              runSearch(term);
            });
            recentSearchesChips.appendChild(chip);
          });
          recentSearchesBlock.hidden = false;
        } else {
          recentSearchesBlock.hidden = true;
        }
      }
    }

    function showDefault() {
      if (defaultBlock) defaultBlock.hidden = false;
      if (statusEl)  statusEl.hidden = true;
      if (countEl)   countEl.hidden = true;
      if (resultsEl) { resultsEl.hidden = true; resultsEl.innerHTML = ''; }
      if (ctaEl)     ctaEl.hidden = true;
      renderRecentDefaults();
    }

    function showStatus(text) {
      if (defaultBlock) defaultBlock.hidden = true;
      if (statusEl) { statusEl.hidden = false; statusEl.textContent = text; }
      if (countEl)   countEl.hidden = true;
      if (resultsEl) resultsEl.hidden = true;
      if (ctaEl)     ctaEl.hidden = true;
    }

    const open = () => {
      wrap.classList.add('is-open');
      wrap.setAttribute('aria-hidden', 'false');
      if (input) { input.setAttribute('aria-expanded', 'true'); setTimeout(() => input.focus(), 80); }
      if (input && !input.value.trim()) showDefault();
    };

    const close = () => {
      wrap.classList.remove('is-open');
      wrap.setAttribute('aria-hidden', 'true');
      if (input) input.setAttribute('aria-expanded', 'false');
    };

    triggers.forEach(t => t.addEventListener('click', () => {
      if (wrap.classList.contains('is-open')) close(); else open();
    }));
    if (closeBtn) closeBtn.addEventListener('click', close);

    document.addEventListener('click', e => {
      if (!wrap.classList.contains('is-open')) return;
      if (wrap.contains(e.target)) return;
      if (triggers.some(t => t.contains(e.target))) return;
      close();
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && wrap.classList.contains('is-open')) {
        close();
        if (triggers[0]) triggers[0].focus();
      }
    });

    /* Guards against a slower earlier response overwriting a faster later one
       (e.g. real fast typing can have two fetches in flight at once) — only
       the response matching the most recently issued request is applied. */
    let searchToken = 0;

    const runSearch = debounce(async (q) => {
      const query = q.trim();
      const token = ++searchToken;
      if (!query || query.length < 2) { showDefault(); return; }

      showStatus('מחפש…');

      try {
        const res = await fetch(`/search?q=${encodeURIComponent(query)}&type=product&section_id=main-search`);
        if (token !== searchToken) return; // a newer query has already superseded this one
        if (!res.ok) throw new Error('search request failed: ' + res.status);
        const html = await res.text();
        if (token !== searchToken) return; // superseded while reading the response body
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const heading = doc.querySelector('.cat-title');
        const countMatch = heading ? heading.textContent.match(/\(([\d,]+)\)/) : null;
        const total = countMatch ? countMatch[1] : null;

        const cards = [...doc.querySelectorAll('.pcard')].slice(0, MAX_RESULTS);

        if (!cards.length) {
          showStatus(`לא נמצאו תוצאות עבור "${query}"`);
          return;
        }

        recordRecentSearch(query);

        if (defaultBlock) defaultBlock.hidden = true;
        if (statusEl) statusEl.hidden = true;

        if (countEl) {
          countEl.hidden = false;
          countEl.textContent = total ? `${total} תוצאות עבור "${query}"` : `תוצאות עבור "${query}"`;
        }

        if (resultsEl) {
          resultsEl.innerHTML = '';
          resultsEl.classList.add('p-grid');
          cards.forEach(card => resultsEl.appendChild(card));
          resultsEl.hidden = false;
          wireProductForms(resultsEl);
        }

        if (ctaEl) {
          ctaEl.href = `/search?q=${encodeURIComponent(query)}&type=product`;
          ctaEl.textContent = total ? `כל ${total} התוצאות עבור "${query}" ←` : `כל התוצאות עבור "${query}" ←`;
          ctaEl.hidden = false;
        }
      } catch (err) {
        showStatus('אירעה שגיאה בחיפוש, נסו שוב');
        console.error('[MAIZ] Search error:', err);
      }
    }, 280);

    if (input) input.addEventListener('input', e => runSearch(e.target.value));

    /* Enter can submit the native <form> (navigating to the full /search
       page) before the debounced fetch above ever resolves — record the
       term here too so a fast Enter still shows up in Recent Searches. */
    const form = input && input.closest('form');
    if (form) form.addEventListener('submit', () => recordRecentSearch(input.value));
  }


  /* ──────────────────────────────────────────────────────
     11. CART — AJAX OPERATIONS
  ────────────────────────────────────────────────────── */

  /* Global MAIZ namespace for cart routes (set in theme.liquid).
     Shopify's AJAX API needs the .js suffix — theme.liquid passes the
     bare routes (/cart/add), which return HTML and broke every add-to-cart. */
  const jsRoute = u => (u && !u.endsWith('.js')) ? u + '.js' : u;
  const rawRoutes = window.MAIZ?.routes || {};
  const routes = {
    cartAdd:    jsRoute(rawRoutes.cartAdd)    || '/cart/add.js',
    cartChange: jsRoute(rawRoutes.cartChange) || '/cart/change.js',
    cart:       jsRoute(rawRoutes.cart)       || '/cart.js',
  };

  async function fetchCart() {
    const res = await fetch(routes.cart || '/cart.js');
    return res.json();
  }

  async function addToCart(variantId, quantity = 1) {
    const res = await fetch(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.description || body.message || 'Add to cart failed');
    }
    return res.json();
  }

  async function changeCartItem(key, quantity) {
    const res = await fetch(routes.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  }

  /* Render cart items in drawer */
  function renderCartItems(items) {
    if (!items.length) {
      return `
        <div class="maiz-cart-empty">
          <svg class="maiz-cart-empty__icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 01-8 0"/>
          </svg>
          <p class="maiz-cart-empty__text">העגלה שלך ריקה</p>
          <button onclick="document.querySelector('.maiz-cart-overlay').click()" class="maiz-btn-ghost maiz-btn--sm">
            המשך לקנות
          </button>
        </div>
      `;
    }

    return items.map(item => `
      <div class="maiz-cart-item" data-key="${item.key}">
        <div class="maiz-cart-item__image">
          ${item.image
            ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy" width="80" height="100">`
            : ''
          }
        </div>
        <div class="maiz-cart-item__info">
          <div class="maiz-cart-item__brand">${item.vendor || ''}</div>
          <div class="maiz-cart-item__name">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div style="font-size:12px;color:var(--maiz-text-tertiary);margin-bottom:4px">${item.variant_title}</div>`
            : ''
          }
          <div class="maiz-cart-item__price">${formatMoney(item.final_line_price)}</div>
          <div class="maiz-cart-item__qty">
            <button class="maiz-qty-btn" data-key="${item.key}" data-action="minus" aria-label="הורד כמות">−</button>
            <span class="maiz-qty-val" aria-live="polite">${item.quantity}</span>
            <button class="maiz-qty-btn" data-key="${item.key}" data-action="plus"  aria-label="הגדל כמות">+</button>
            <button class="maiz-remove-btn" data-key="${item.key}" aria-label="הסר מוצר">הסר</button>
          </div>
        </div>
      </div>
    `).join('');
  }

  /* ── CART DRAWER ── */
  function initCart() {
    const overlay  = $('.maiz-cart-overlay');
    const drawer   = $('.maiz-cart-drawer');
    const closeBtn = $('.maiz-cart-drawer__close');
    const body     = $('.maiz-cart-drawer__body');
    const footer   = $('[data-cart-footer]');
    const totalEl  = $('[data-cart-total]');
    const countEls = $$('[data-cart-count]');

    if (!drawer) return;

    const openCart = async () => {
      overlay && overlay.classList.add('is-open');
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      await refreshCart();
    };

    const closeCart = () => {
      overlay && overlay.classList.remove('is-open');
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    /* Triggers */
    $$('[data-cart-open]').forEach(t => t.addEventListener('click', openCart));
    if (overlay)  overlay.addEventListener('click',  closeCart);
    if (closeBtn) closeBtn.addEventListener('click', closeCart);
    $$('[data-close-cart]').forEach(t => t.addEventListener('click', closeCart));

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeCart();
    });

    /* Refresh cart UI */
    async function refreshCart() {
      const cart = await fetchCart();

      /* Update count badges */
      countEls.forEach(el => {
        el.textContent = cart.item_count;
        el.dataset.count = cart.item_count;
      });

      /* Bounce badge */
      countEls.forEach(el => {
        if (cart.item_count > 0) {
          el.classList.remove('is-bouncing');
          void el.offsetWidth; // reflow
          el.classList.add('is-bouncing');
        }
      });

      /* Update total */
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

      /* Hide checkout/total footer while the cart is empty — the empty-state
         inside the body already has its own "continue shopping" CTA, and a
         live checkout button pointing at a ₪0 order is misleading. */
      if (footer) footer.classList.toggle('is-empty', cart.item_count === 0);

      /* Render items */
      if (body) {
        body.innerHTML = renderCartItems(cart.items);
        attachCartListeners(body);
      }

      return cart;
    }

    function attachCartListeners(container) {
      /* Quantity buttons */
      $$('.maiz-qty-btn', container).forEach(btn => {
        btn.addEventListener('click', async () => {
          const key    = btn.dataset.key;
          const action = btn.dataset.action;
          const item   = btn.closest('.maiz-cart-item');
          const qtyEl  = item && $('.maiz-qty-val', item);
          if (!qtyEl) return;

          const current = parseInt(qtyEl.textContent, 10);
          const next    = action === 'plus' ? current + 1 : Math.max(0, current - 1);

          btn.disabled = true;
          try {
            await changeCartItem(key, next);
            await refreshCart();
          } finally {
            btn.disabled = false;
          }
        });
      });

      /* Remove buttons */
      $$('.maiz-remove-btn', container).forEach(btn => {
        btn.addEventListener('click', async () => {
          const key = btn.dataset.key;
          btn.disabled = true;
          try {
            await changeCartItem(key, 0);
            await refreshCart();
          } finally {
            btn.disabled = false;
          }
        });
      });
    }

    /* Global cart refresh — called after any add-to-cart */
    window.MAIZ = window.MAIZ || {};
    window.MAIZ.refreshCart = refreshCart;
    window.MAIZ.openCart    = openCart;
  }


  /* ──────────────────────────────────────────────────────
     11b. PDP VARIANT SELECTOR
     The option <select>s never updated the hidden variant id, price,
     or stock -- picking any non-default option (e.g. a paid installation
     tier) silently added the DEFAULT variant instead, at the default price.
  ────────────────────────────────────────────────────── */

  function initVariantSelector() {
    $$('[data-product-form]').forEach(form => {
      const script = $('[data-product-variants]', form);
      if (!script) return; // single-variant product, nothing to wire up

      let variants;
      try { variants = JSON.parse(script.textContent); } catch { return; }

      const selects  = $$('.option-group select', form);
      const idInput  = $('[name="id"]', form);
      const priceEl  = $('[data-current-price]');
      const compareWrap = $('[data-price-compare-wrap]');
      const comparePriceEl = $('[data-compare-price]');
      const saveTextEl = $('[data-save-text]');
      const stockEl  = $('[data-stock-status]');
      const priceMeta = $('[data-price-meta]');
      const availMeta = $('[data-availability-meta]');
      const btn      = $('[data-atc-btn]', form);
      const btnText  = btn && $('[data-atc-btn-text]', btn);
      // Buy Now lives outside the <form>...</form> markup (linked via form="id" instead,
      // since a moved/relocated submit button keeps working via that attribute regardless
      // of DOM position) — so it must be looked up by the form's id, not as a descendant.
      const buyNowBtn = form.id ? document.querySelector(`[data-buynow-btn][form="${form.id}"]`) : null;
      const buyNowBtnText = buyNowBtn && $('[data-buynow-btn-text]', buyNowBtn);

      const findVariant = () => {
        const selected = selects.map(s => s.value);
        return variants.find(v => selected.every((val, i) => v.options[i] === val));
      };

      const applyVariant = (variant) => {
        if (!variant) {
          if (idInput) idInput.value = '';
          if (btn) { btn.disabled = true; }
          if (btnText) btnText.textContent = 'לא זמין בשילוב הזה';
          if (buyNowBtn) { buyNowBtn.disabled = true; buyNowBtn.setAttribute('aria-disabled', 'true'); }
          if (buyNowBtnText) buyNowBtnText.textContent = 'לא זמין בשילוב הזה';
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (priceMeta) priceMeta.setAttribute('content', variant.price / 100);

        const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (compareWrap) compareWrap.hidden = !hasCompare;
        if (hasCompare) {
          if (comparePriceEl) comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          if (saveTextEl) {
            const save = variant.compare_at_price - variant.price;
            const pct = Math.round((save / variant.compare_at_price) * 100);
            saveTextEl.textContent = `🔥 חיסכון של ${formatMoney(save)} (${pct}% הנחה)`;
          }
        }

        if (stockEl) {
          stockEl.textContent = variant.available ? '✓ במלאי' : '✗ אזל מהמלאי';
          stockEl.classList.toggle('out-of-stock', !variant.available);
        }
        if (availMeta) availMeta.setAttribute('href', `https://schema.org/${variant.available ? 'InStock' : 'OutOfStock'}`);

        if (btn) btn.disabled = !variant.available;
        if (btnText) btnText.textContent = variant.available ? '🛒 הוסף לעגלה' : 'אזל מהמלאי';

        if (buyNowBtn) {
          buyNowBtn.disabled = !variant.available;
          buyNowBtn.setAttribute('aria-disabled', (!variant.available).toString());
        }
        if (buyNowBtnText) buyNowBtnText.textContent = variant.available ? 'קנה עכשיו' : 'אזל מהמלאי';

        // Gallery sync: show the image assigned to this variant, if it has one.
        // Falls back to leaving the current image alone when the variant has no
        // featured_image (e.g. single-image products, or a variant that shares
        // the default photo) -- never blank the gallery out.
        if (variant.featured_image) {
          const mainImg = document.getElementById('MainProductImage');
          const basePath = url => (url || '').split('?')[0];
          const targetPath = basePath(variant.featured_image.src);
          const thumbs = $$('.d-thumb');
          const matchThumb = thumbs.find(t => basePath(t.dataset.src) === targetPath);

          if (mainImg) {
            if (matchThumb) {
              mainImg.src = matchThumb.dataset.src;
            } else {
              const sep = variant.featured_image.src.includes('?') ? '&' : '?';
              mainImg.src = `${variant.featured_image.src}${sep}width=1200`;
            }
            mainImg.removeAttribute('srcset');
          }
          if (matchThumb) thumbs.forEach(t => t.classList.toggle('active', t === matchThumb));
        }
      };

      selects.forEach(sel => sel.addEventListener('change', () => applyVariant(findVariant())));

      // Native-variant color swatches (PDP V3): each swatch mirrors its value
      // into the real (visually-hidden) <select> and dispatches a native
      // 'change' event, so the exact same applyVariant()/findVariant() logic
      // above runs — no separate variant-switching code path to keep in sync.
      $$('[data-swatch-row]', form).forEach(row => {
        const optionIndex = row.dataset.optionIndex;
        const pairedSelect = selects[optionIndex];
        if (!pairedSelect) return;
        const swatches = $$('.swatch-chip', row);
        swatches.forEach(sw => {
          sw.addEventListener('click', () => {
            pairedSelect.value = sw.dataset.swatchValue;
            pairedSelect.dispatchEvent(new Event('change', { bubbles: true }));
            swatches.forEach(s => { s.classList.remove('active'); s.setAttribute('aria-checked', 'false'); });
            sw.classList.add('active');
            sw.setAttribute('aria-checked', 'true');
          });
        });
      });
    });
  }


  /* ──────────────────────────────────────────────────────
     12. ADD TO CART — PRODUCT FORMS
  ────────────────────────────────────────────────────── */

  function bindProductForm(form) {
      form.addEventListener('submit', async e => {
        /* The Buy Now button (name="checkout") must submit natively so Shopify's
           own /cart/add -> /checkout redirect happens — it must NOT be intercepted
           by the AJAX add-to-cart flow below, which is for the Add to Cart button only. */
        if (e.submitter && e.submitter.matches('[data-buynow-btn]')) {
          if (e.submitter.disabled || e.submitter.classList.contains('is-loading')) {
            // Already submitting (or a repeat click before navigation lands) — block it.
            e.preventDefault();
            return;
          }
          e.submitter.classList.add('is-loading');
          e.submitter.innerHTML = '<span class="sr-only">מעביר לתשלום...</span><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation:spin 0.8s linear infinite"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke-opacity="0.25"/><path d="M21 12c0-4.97-4.03-9-9-9"/></svg>';
          // Disable AFTER this submission is already underway, so the current native
          // submit still goes through while any further click is rejected outright.
          setTimeout(() => { e.submitter.disabled = true; }, 0);
          // Native form submission continues — the browser is about to navigate away.
          return;
        }

        e.preventDefault();

        const btn      = $('[data-atc-btn]', form) || $('button[type="submit"]', form);
        const varInput = $('[name="id"]', form);

        if (!btn || !varInput) return;

        const variantId = varInput.value;
        if (!variantId) return;

        /* Loading state — capture full markup so icon buttons restore intact */
        const originalText = btn.innerHTML;
        btn.disabled = true;
        btn.classList.add('is-loading');
        btn.innerHTML = '<span class="sr-only">טוען...</span><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation:spin 0.8s linear infinite"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke-opacity="0.25"/><path d="M21 12c0-4.97-4.03-9-9-9"/></svg>';

        try {
          await addToCart(variantId, 1);

          /* Success state */
          btn.classList.remove('is-loading');
          btn.classList.add('is-success');
          btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> <span>נוסף!</span>`;

          /* Refresh cart count */
          if (window.MAIZ?.refreshCart) await window.MAIZ.refreshCart();

          /* Open cart drawer after brief delay */
          setTimeout(() => {
            if (window.MAIZ?.openCart) window.MAIZ.openCart();
          }, 600);

          /* Reset button */
          setTimeout(() => {
            btn.classList.remove('is-success');
            btn.disabled = false;
            btn.innerHTML = originalText;
          }, 2000);

        } catch (err) {
          /* Same button-swap pattern as the success state, so a failed add
             (e.g. out of stock) is visible instead of silently resetting. */
          btn.classList.remove('is-loading');
          btn.innerHTML = `<span>⚠ ${err.message || 'שגיאה, נסה שוב'}</span>`;
          setTimeout(() => {
            btn.disabled = false;
            btn.innerHTML = originalText;
          }, 2500);
          console.error('[MAIZ] Add to cart error:', err);
        }
      });
  }

  /* Wire every [data-product-form] found within root (defaults to the whole
     document) — used both at page-load init and for cards injected later
     (e.g. live search results), so add-to-cart logic is never duplicated. */
  function wireProductForms(root) {
    $$('[data-product-form]', root).forEach(bindProductForm);
  }

  function initProductForms() {
    wireProductForms(document);
  }


  /* ──────────────────────────────────────────────────────
     13. PRODUCT GALLERY — LIGHTBOX
     Phase 1 fix: this previously targeted [data-gallery]/.maiz-gallery-main/
     .maiz-gallery-thumb markup that no template ever provided — confirmed via
     full-codebase grep that it was 100% dead code, never reaching any real
     product page. Rewritten to target the PDP's actual gallery markup
     (.d-main-img[data-lightbox-trigger] / #MainProductImage) and wire it to
     the existing globally-injected .maiz-lightbox modal (layout/theme.liquid),
     which was already correctly built but never triggered by anything.
     Thumbnail click-to-switch behavior is unchanged — it's handled by the
     existing inline script in main-product.liquid and isn't touched here.
  ────────────────────────────────────────────────────── */

  function initGallery() {
    const trigger  = $('[data-lightbox-trigger]');
    const lightbox = $('.maiz-lightbox');
    if (!trigger || !lightbox) return;

    const lbImg   = $('.maiz-lightbox__image', lightbox);
    const lbClose = $('.maiz-lightbox__close', lightbox);
    if (!lbImg) return;

    const openLb = () => {
      const mainImg = document.getElementById('MainProductImage');
      if (!mainImg) return;
      lbImg.src = mainImg.currentSrc || mainImg.src;
      lbImg.alt = mainImg.alt || '';
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      (lbClose || lightbox).focus();
    };

    const closeLb = () => {
      if (!lightbox.classList.contains('is-open')) return;
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      trigger.focus();
    };

    trigger.addEventListener('click', openLb);
    trigger.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openLb();
      }
    });

    if (lbClose) lbClose.addEventListener('click', closeLb);
    lightbox.addEventListener('click', e => { if (e.target === lightbox) closeLb(); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && lightbox.classList.contains('is-open')) closeLb();
    });
  }


  /* ──────────────────────────────────────────────────────
     13b. PDP VIDEO FACADES — click-to-load, no autoplay on page load
     (PDP V2). Each .video-facade button is replaced with a real iframe only
     when clicked — nothing loads or counts as page weight until then.
  ────────────────────────────────────────────────────── */

  function initVideoFacades() {
    $$('[data-video-facade]').forEach(btn => {
      btn.addEventListener('click', () => {
        const url = btn.dataset.videoUrl;
        const title = btn.getAttribute('aria-label') || 'וידאו מוצר';
        let embedSrc = null;

        if (url.includes('youtube.com') || url.includes('youtu.be')) {
          const id = url.includes('youtu.be/')
            ? url.split('youtu.be/')[1].split('?')[0]
            : url.split('v=')[1]?.split('&')[0];
          if (id) embedSrc = `https://www.youtube.com/embed/${id}?autoplay=1`;
        } else if (url.includes('vimeo.com')) {
          const id = url.split('vimeo.com/')[1]?.split('?')[0];
          if (id) embedSrc = `https://player.vimeo.com/video/${id}?autoplay=1`;
        }

        const wrap = document.createElement('div');
        wrap.className = 'video-embed-frame';
        if (embedSrc) {
          wrap.innerHTML = `<iframe src="${embedSrc}" title="${title.replace(/"/g, '&quot;')}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
          btn.replaceWith(wrap);
        } else {
          // Unrecognized host (not YouTube/Vimeo) — can't embed generically, open in a new tab instead.
          window.open(url, '_blank', 'noopener');
        }
      });
    });
  }


  /* ──────────────────────────────────────────────────────
     14. STICKY ATC — shows when product CTA scrolls out
  ────────────────────────────────────────────────────── */

  function initStickyAtc() {
    const trigger = $('[data-sticky-atc-trigger]');
    const sticky  = $('.maiz-sticky-atc');

    if (!trigger || !sticky) return;

    const observer = new IntersectionObserver(([entry]) => {
      sticky.classList.toggle('is-visible', !entry.isIntersecting);
    }, { threshold: 0 });

    observer.observe(trigger);
  }


  /* ──────────────────────────────────────────────────────
     15. FILTER PILLS — collection page
  ────────────────────────────────────────────────────── */

  function initFilters() {
    $$('[data-filter-group]').forEach(group => {
      const pills = $$('.maiz-filter-pill', group);
      pills.forEach(pill => {
        pill.addEventListener('click', () => {
          pills.forEach(p => p.classList.remove('is-active'));
          pill.classList.add('is-active');
          /* Dispatch event — Shopify filtering picks this up */
          const event = new CustomEvent('maiz:filter', {
            detail: { value: pill.dataset.value, group: group.dataset.filterGroup },
            bubbles: true,
          });
          group.dispatchEvent(event);
        });
      });
    });
  }


  /* ──────────────────────────────────────────────────────
     16. SPIN KEYFRAME (for loading state)
  ────────────────────────────────────────────────────── */

  (function injectSpinKeyframe() {
    if (document.querySelector('#maiz-spin-kf')) return;
    const style = document.createElement('style');
    style.id = 'maiz-spin-kf';
    style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  })();


  /* ──────────────────────────────────────────────────────
     INIT — run all systems on DOMContentLoaded
  ────────────────────────────────────────────────────── */

  function init() {
    recordRecentlyViewed();
    initReveal();
    initCounters();
    initParallax();
    initTilt();
    initNav();
    initMobileNav();
    initHero();
    initChipStrip();
    initPolicyPage();
    initHotspots();
    initCarousels();
    initHeaderSearch();
    initCart();
    initVariantSelector();
    initProductForms();
    initGallery();
    initVideoFacades();
    initStickyAtc();
    initFilters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* ── Commerce Module (System B) ──────────────────────────────────────────────
   Handles: sort select, sidebar toggle, PDP gallery, variant selector,
            predictive search. Runs independently of the MAIZ Motion Engine.
   Class namespaces: .sidebar, .d-thumb, .product-form, .price-big etc.
   Zero overlap with MAIZ (.maiz-*, .mch__*) selectors.
─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return [...(ctx || document).querySelectorAll(sel)]; }
  const fmt = window.formatMoney; // delegate to the single shared formatter (was a separate, always-0-decimal duplicate)

  /* ── Sort select ── */
  const sortSel = document.getElementById('SortBy');
  if (sortSel) {
    sortSel.addEventListener('change', function () {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.value);
      window.location.href = url.toString();
    });
  }

  /* ── Commerce PDP gallery thumbnails (.d-thumb → #MainProductImage) ── */
  $$('.d-thumb').forEach(btn => {
    btn.addEventListener('click', function () {
      const mainImg = document.getElementById('MainProductImage');
      if (mainImg && this.dataset.src) {
        mainImg.src = this.dataset.src;
        mainImg.removeAttribute('srcset');
      }
      $$('.d-thumb').forEach(b => b.classList.remove('active'));
      this.classList.add('active');
    });
  });

  /* ── Mobile sidebar toggle ── */
  const sidebarToggle = $('[data-sidebar-toggle]');
  const sidebar = $('.sidebar');
  if (sidebarToggle && sidebar) {
    sidebarToggle.addEventListener('click', function () {
      const open = sidebar.classList.toggle('sidebar-open');
      this.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ── Commerce variant selector (PDP) ── */
  (function setupVariants() {
    const selects = $$('.product-form select');
    if (!selects.length || !window.productVariants) return;
    const variants = window.productVariants;

    function getSelected() {
      const opts = selects.map(s => s.value);
      return variants.find(v => v.options.every((o, i) => o === opts[i]));
    }

    function update(v) {
      const idInput = $('[data-variant-id]');
      const buyBtn  = $('.btn-buy');
      const priceEl = $('.price-big');
      const oldWrap = $('.price-old-d');
      const oldEl   = oldWrap ? $('s', oldWrap) : null;
      const savEl   = $('.price-sav');
      if (!v) { if (buyBtn) { buyBtn.disabled = true; buyBtn.textContent = 'לא זמין'; } return; }
      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.textContent = fmt(v.price);
      if (oldEl && v.compare_at_price > v.price) {
        oldEl.textContent = fmt(v.compare_at_price);
        if (oldWrap) oldWrap.style.display = '';
      } else if (oldWrap) { oldWrap.style.display = 'none'; }
      if (savEl) {
        if (v.compare_at_price > v.price) {
          const save = v.compare_at_price - v.price;
          const pct  = Math.round(save * 100 / v.compare_at_price);
          savEl.textContent = '🔥 חיסכון של ' + fmt(save) + ' (' + pct + '% הנחה)';
          savEl.style.display = '';
        } else { savEl.style.display = 'none'; }
      }
      if (buyBtn) { buyBtn.disabled = !v.available; buyBtn.textContent = v.available ? '🛒 הוסף לעגלה' : 'אזל מהמלאי'; }
    }

    selects.forEach(sel => sel.addEventListener('change', () => { const v = getSelected(); if (v) update(v); }));
    const cur = getSelected();
    if (cur) update(cur);
  })();

  /* ── Predictive search (client-side product filter) ── */
  (function setupSearch() {
    const inp = document.getElementById('SearchInput');
    const res = document.getElementById('SearchResults');
    if (!inp || !res) return;

    let prods = [], loaded = false, timer;

    function load() {
      if (loaded) return Promise.resolve();
      return fetch('/collections/all/products.json?limit=250')
        .then(r => r.json())
        .then(d => { prods = d.products || []; loaded = true; })
        .catch(() => {});
    }

    function card(p) {
      const img = p.images && p.images[0] ? p.images[0].src : '';
      const v   = p.variants && p.variants[0];
      const price = v ? fmt(parseInt(v.price) * 100) : '';
      let vendor = p.vendor || '';
      if (vendor === 'עולם הסאונד' || vendor === 'Olam HaSound') vendor = 'MAIZ';
      return '<a href="/products/' + p.handle + '" style="display:flex;align-items:center;gap:12px;padding:12px 16px;text-decoration:none;border-bottom:1px solid #f1f5f9;color:inherit" onmouseover="this.style.background=\'#fff7ed\'" onmouseout="this.style.background=\'\'">'
        + (img ? '<img src="' + img + '&width=80" style="width:48px;height:48px;object-fit:contain;border-radius:8px;background:#f5f5f0;flex-shrink:0" loading="lazy">'
               : '<div style="width:48px;height:48px;background:#f5f5f0;border-radius:8px;flex-shrink:0"></div>')
        + '<div style="flex:1;min-width:0"><div style="font-size:13.5px;font-weight:700;color:#1e293b;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + p.title + '</div>'
        + '<div style="font-size:11px;color:#f97316;font-weight:700;margin-top:2px">' + (vendor ? vendor + ' · ' : '') + price + '</div></div></a>';
    }

    function show(q) {
      const matches = q
        ? prods.filter(p => p.title.toLowerCase().includes(q.toLowerCase()) || (p.vendor || '').toLowerCase().includes(q.toLowerCase())).slice(0, 6)
        : prods.slice(0, 6);
      if (!matches.length) { res.style.display = 'none'; return; }
      res.innerHTML = matches.map(card).join('')
        + '<a href="/search?q=' + encodeURIComponent(q || '') + '" style="display:block;padding:12px 16px;text-align:center;font-size:13px;font-weight:700;color:#f97316;text-decoration:none;background:#fff7ed;border-radius:0 0 14px 14px">'
        + (q ? 'כל תוצאות החיפוש ←' : 'כל המוצרים ←') + '</a>';
      res.style.display = 'block';
    }

    load();
    inp.addEventListener('focus', () => load().then(() => show('')));
    inp.addEventListener('input', function () { clearTimeout(timer); const q = this.value.trim(); timer = setTimeout(() => show(q), 200); });
    inp.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') { res.style.display = 'none'; inp.blur(); }
      if (e.key === 'Enter' && this.value.trim()) window.location.href = '/search?q=' + encodeURIComponent(this.value.trim());
    });
    document.addEventListener('click', e => { if (!inp.contains(e.target) && !res.contains(e.target)) res.style.display = 'none'; });
  })();

})();
